<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import AnalyticsPanel from './AnalyticsPanel.vue';
import MetricValue from './MetricValue.vue';
import { dayLabel, fillDays } from './analyticsHelpers';
import { useAnalyticsChart } from './useAnalyticsChart';
import { useAnalyticsFormat } from './useAnalyticsFormat';
import { QUEM_CORES } from '../atendimento/quemCores';

const props = defineProps({
  // [{ day, ai_conversations, ai_messages, human_messages }]
  daily: { type: Array, default: null },
  since: { type: String, default: '' },
  until: { type: String, default: '' },
});

const { t } = useI18n();
const { number } = useAnalyticsFormat();
const { palette, bar, chartOptions } = useAnalyticsChart();

const days = computed(() =>
  fillDays(props.daily || [], props.since, props.until, {
    ai_conversations: 0,
    ai_messages: 0,
    human_messages: 0,
  })
);
const labels = computed(() => days.value.map(row => dayLabel(row.day)));
const series = key => days.value.map(row => row[key]);

// Um gráfico sem nenhum valor acima de zero não mede nada: vira uma frase.
const hasMessages = computed(() =>
  days.value.some(row => row.ai_messages > 0 || row.human_messages > 0)
);
const hasConversations = computed(() =>
  days.value.some(row => row.ai_conversations > 0)
);

const messagesCollection = computed(() => ({
  labels: labels.value,
  datasets: [
    bar(
      t('ANALYTICS_REPORTS.DAILY.AI'),
      series('ai_messages'),
      palette.value.ia
    ),
    bar(
      t('ANALYTICS_REPORTS.DAILY.PEOPLE'),
      series('human_messages'),
      palette.value.pessoa
    ),
  ],
}));

const conversationsCollection = computed(() => ({
  labels: labels.value,
  datasets: [
    bar(
      t('ANALYTICS_REPORTS.DAILY.CONVERSATIONS'),
      series('ai_conversations'),
      palette.value.ia
    ),
  ],
}));

const options = computed(() => chartOptions(number));
</script>

<template>
  <AnalyticsPanel :title="t('ANALYTICS_REPORTS.DAILY.TITLE')">
    <MetricValue v-if="!daily" />
    <div v-else class="grid gap-6 lg:grid-cols-2">
      <div class="flex flex-col gap-2 min-w-0">
        <div
          class="flex flex-wrap gap-x-4 gap-y-1 justify-between items-center"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('ANALYTICS_REPORTS.DAILY.MESSAGES') }}
          </h3>
          <div class="flex gap-3 items-center text-xs text-n-slate-11">
            <span class="flex gap-1.5 items-center">
              <span class="rounded-sm size-2.5" :class="QUEM_CORES.ia.bg" />
              {{ t('ANALYTICS_REPORTS.DAILY.AI') }}
            </span>
            <span class="flex gap-1.5 items-center">
              <span class="rounded-sm size-2.5" :class="QUEM_CORES.pessoa.bg" />
              {{ t('ANALYTICS_REPORTS.DAILY.PEOPLE') }}
            </span>
          </div>
        </div>
        <div class="relative h-56">
          <BarChart
            v-if="hasMessages"
            :collection="messagesCollection"
            :chart-options="options"
          />
          <p
            v-else
            class="flex justify-center items-center mb-0 h-full text-sm text-n-slate-11"
          >
            {{ t('ANALYTICS_REPORTS.DAILY.EMPTY') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2 min-w-0">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYTICS_REPORTS.DAILY.CONVERSATIONS') }}
        </h3>
        <div class="relative h-56">
          <BarChart
            v-if="hasConversations"
            :collection="conversationsCollection"
            :chart-options="options"
          />
          <p
            v-else
            class="flex justify-center items-center mb-0 h-full text-sm text-n-slate-11"
          >
            {{ t('ANALYTICS_REPORTS.DAILY.EMPTY') }}
          </p>
        </div>
      </div>
    </div>
  </AnalyticsPanel>
</template>
